import React, { useMemo } from 'react';
import Select, { ValueType } from 'react-select';
import { LeadConfigModel } from '../../../../shared/models/lead.model';
import { OpsDateBasis, OpsFilterModel } from '../../../../shared/models/lead-ops.model';
import { StaffPicker } from '../../../../shared/components/group-ui/staff-picker/staff-picker.component';

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};
const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';
const INPUT = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm';

interface Option {
  value: string;
  label: string;
}

const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const toOptions = (values: string[]): Option[] =>
  values.map((value: string) => ({ value, label: titleCase(value) }));

export const OpsFilterBar: React.FC<{
  filter: OpsFilterModel;
  config?: LeadConfigModel;
  onChange: (patch: Partial<OpsFilterModel>) => void;
}> = ({ filter, config, onChange }) => {
  const verticalOptions = useMemo(() => toOptions(config?.verticals ?? []), [config]);
  const statusOptions = useMemo(() => toOptions(config?.statuses ?? []), [config]);
  const valueOptions = useMemo(() => toOptions(config?.valueCategories ?? []), [config]);

  const multi = (
    key: 'vertical' | 'status' | 'valueCategory',
    label: string,
    options: Option[]
  ) => (
    <div>
      <label className={LABEL}>{label}</label>
      <Select
        isMulti
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={selectMenuPortalStyles}
        classNamePrefix="react-select"
        options={options}
        placeholder="All"
        value={options.filter((option) => (filter[key] ?? []).includes(option.value))}
        onChange={(value: ValueType<Option, true>) =>
          onChange({ [key]: ((value as Option[]) || []).map((o) => o.value) })
        }
      />
    </div>
  );

  return (
    <section className="rounded-2xl border border-creams-600 bg-white p-5 shadow-md">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div>
          <label className={LABEL}>From</label>
          <input
            className={INPUT}
            type="date"
            value={filter.dateFrom ?? ''}
            onChange={(event) => onChange({ dateFrom: event.target.value || undefined })}
          />
        </div>
        <div>
          <label className={LABEL}>To</label>
          <input
            className={INPUT}
            type="date"
            value={filter.dateTo ?? ''}
            onChange={(event) => onChange({ dateTo: event.target.value || undefined })}
          />
        </div>
        <div>
          {/*
            Not a cosmetic toggle. "How many did we take in March" and "how many
            did we close in March" are different windows over the same leads,
            and every number on the page moves with it.
          */}
          <label className={LABEL}>Dates measured by</label>
          <Select
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            options={[
              { value: OpsDateBasis.CREATED, label: 'When the lead arrived' },
              { value: OpsDateBasis.RESOLVED, label: 'When it was closed' }
            ]}
            value={
              filter.dateBasis === OpsDateBasis.RESOLVED
                ? { value: OpsDateBasis.RESOLVED, label: 'When it was closed' }
                : { value: OpsDateBasis.CREATED, label: 'When the lead arrived' }
            }
            onChange={(option: ValueType<{ value: OpsDateBasis; label: string }, false>) =>
              onChange({
                dateBasis: option ? (option as { value: OpsDateBasis }).value : OpsDateBasis.CREATED
              })
            }
          />
        </div>
        <div>
          <label className={LABEL}>Assignee</label>
          <StaffPicker
            value={filter.assignedTo?.[0]}
            onChange={(userId?: string) => onChange({ assignedTo: userId ? [userId] : [] })}
            placeholder="Anyone"
          />
        </div>

        {multi('vertical', 'Verticals', verticalOptions)}
        {multi('status', 'Statuses', statusOptions)}
        {multi('valueCategory', 'Value tiers', valueOptions)}
      </div>
    </section>
  );
};
